import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const ScrollTextReveal = ({ 
  children, 
  className = "",
  containerHeight = "200vh",
  stickyHeight = "50%",
  textClassName = "",
  maxWidth = "max-w-5xl"
}) => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
  });

  if (typeof children !== "string") {
    throw new Error("ScrollTextReveal: children must be a string");
  }

  const words = children.split(" ");

  return (
    <div 
      ref={targetRef} 
      className={`relative z-0 ${className}`}
      style={{ height: containerHeight }}
    >
      <div
        className={`sticky top-0 mx-auto flex items-center justify-center bg-transparent px-[1rem] py-[5rem] ${maxWidth}`}
        style={{ height: stickyHeight }}
      >
        <span
          className={`flex flex-wrap text-center p-5 text-xl font-medium text-Color-Black/20 md:p-8 md:text-2xl lg:p-10 lg:text-3xl xl:text-4xl font-['Montserrat'] ${textClassName}`}
        >
          {words.map((word, i) => {
            const start = i / words.length;
            const end = start + 1 / words.length;
            return (
              <Word key={i} progress={scrollYProgress} range={[start, end]}>
                {word}
              </Word>
            );
          })}
        </span>
      </div>
    </div>
  );
};

const Word = ({ children, progress, range }) => {
  const opacity = useTransform(progress, range, [0.15, 1]);
  const scale = useTransform(progress, range, [0.95, 1]);
  
  return (
    <span className="relative mx-1 lg:mx-2">
      <span className="absolute opacity-15 text-Color-Black/20">{children}</span>
      <motion.span
        style={{ opacity, scale }}
        className="text-Color-Black font-medium inline-block"
      >
        {children}
      </motion.span>
    </span>
  );
};

// Inline version for use within existing components
export const InlineScrollTextReveal = ({ 
  children, 
  className = "",
  textClassName = ""
}) => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start 0.8", "end 0.2"]
  });

  if (typeof children !== "string") {
    throw new Error("InlineScrollTextReveal: children must be a string");
  }

  const words = children.split(" ");

  return (
    <div 
      ref={targetRef} 
      className={`relative ${className}`}
    >
      <span
        className={`flex flex-wrap text-Color-Black/20 font-medium font-['Montserrat'] ${textClassName}`}
      >
        {words.map((word, i) => {
          const start = i / words.length;
          const end = start + 1 / words.length;
          return (
            <InlineWord key={i} progress={scrollYProgress} range={[start, end]}>
              {word}
            </InlineWord>
          );
        })}
      </span>
    </div>
  );
};

const InlineWord = ({ children, progress, range }) => {
  const opacity = useTransform(progress, range, [0.2, 1]);
  
  return (
    <span className="relative mx-1">
      <span className="absolute opacity-20 text-Color-Black/20">{children}</span>
      <motion.span
        style={{ opacity }}
        className="text-Color-Black font-medium"
      >
        {children}
      </motion.span>
    </span>
  );
};

export default ScrollTextReveal;
